import { EventType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { CloseOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { styled } from '@mui/system';
import * as Sentry from '@sentry/react';
import {
    closeSnackbar,
    MaterialDesignContent,
    SnackbarProvider,
    type CustomContentProps,
} from 'notistack';
import { forwardRef, useEffect } from 'react';
import AppErrorFallback from '@/components/AppErrorFallback';
import { b2cPolicies, protectedResources } from './authConfig';
import Routes from './Routes';
import { ThemeProvider, theme } from './theme';

/**
 * Compare the token issuing policy with a specific policy name
 */
function compareIssuingPolicy(
    idTokenClaims: Record<string, string>,
    policyToCompare: string,
): boolean {
    const tfpMatches =
        'tfp' in idTokenClaims && idTokenClaims.tfp.toLowerCase() === policyToCompare.toLowerCase();
    const acrMatches =
        'acr' in idTokenClaims && idTokenClaims.acr.toLowerCase() === policyToCompare.toLowerCase();
    return tfpMatches || acrMatches;
}

const StyledSnackbarContent = styled(MaterialDesignContent)(() => ({
    // Beat notistack/goober's `flexWrap: wrap` without targeting hashed class names.
    '&&': {
        display: 'flex',
        flexWrap: 'nowrap',
        maxWidth: '100%',
    },
    '#notistack-snackbar': {
        flex: 1,
        minWidth: 0,
    },
    '#notistack-snackbar a': {
        color: 'inherit',
        overflowWrap: 'anywhere',
    },
    '&.notistack-MuiContent-success': {
        backgroundColor: theme.palette.success.S_50,
        color: theme.palette.neutral.black,
        'svg path': {
            fill: theme.palette.success.main,
        },
    },
    '&.notistack-MuiContent-warning': {
        backgroundColor: theme.palette.warning.W_50,
        color: theme.palette.neutral.black,
        'svg path': {
            fill: theme.palette.warning.main,
        },
    },
    '&.notistack-MuiContent-error': {
        backgroundColor: theme.palette.error.E_50,
        color: theme.palette.neutral.black,
        'svg path': {
            fill: theme.palette.error.main,
        },
    },
    '&.notistack-MuiContent-info': {
        backgroundColor: theme.palette.primary.P_50,
        color: theme.palette.neutral.black,
        'svg path': {
            fill: theme.palette.primary.main,
        },
    },
}));

// Notistack's message row is display:flex. Browsers often turn emails into
// mailto links, which splits the text into flex items and crushes the rest
// of the sentence on narrow screens. Wrap the message so it flows as text.
const WrappingSnackbarContent = forwardRef<HTMLDivElement, CustomContentProps>(
    function WrappingSnackbarContent({ message, ...props }, ref) {
        return (
            <StyledSnackbarContent
                ref={ref}
                {...props}
                message={
                    <span
                        style={{
                            flex: 1,
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                            whiteSpace: 'normal',
                        }}
                    >
                        {message}
                    </span>
                }
            />
        );
    },
);

export default function App() {
    /**
     * useMsal is hook that returns the PublicClientApplication instance,
     * an array of all accounts currently signed in and an inProgress value
     * that tells you what msal is currently doing. For more, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
     */
    const { instance, accounts } = useMsal();

    useEffect(() => {
        const callbackId = instance.addEventCallback((event) => {
            if (
                (event.eventType === EventType.LOGIN_SUCCESS ||
                    event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
                event.payload.account
            ) {
                /**
                 * Below we are checking if the user is returning from the reset password flow.
                 * If so, we will ask the user to reauthenticate with their new password.
                 * If you do not want this behavior and prefer your users to stay signed in instead,
                 * you can replace the code below with the same pattern used for handling the return from
                 * profile edit flow
                 */
                if (
                    compareIssuingPolicy(
                        event.payload.idTokenClaims,
                        b2cPolicies.names.forgotPassword,
                    )
                ) {
                    const signUpSignInFlowRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        scopes: [...protectedResources.backend.scopes.api],
                    };
                    instance.loginRedirect(signUpSignInFlowRequest);
                }
            }

            if (event.eventType === EventType.LOGIN_FAILURE) {
                // Check for forgot password error
                // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
                if (event.error?.errorMessage.includes('AADB2C90118')) {
                    const resetPasswordRequest = {
                        authority: b2cPolicies.authorities.forgotPassword.authority,
                        scopes: [],
                    };
                    instance.loginRedirect(resetPasswordRequest);
                }
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance]);

    useEffect(() => {
        // If accounts are already cached (e.g. redirected from old prod), set active account.
        if (accounts.length > 0 && !instance.getActiveAccount()) {
            instance.setActiveAccount(accounts[0]);
        }
    }, [accounts, instance]);

    return (
        <ThemeProvider theme={theme}>
            <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
                <Routes />
            </Sentry.ErrorBoundary>
            <SnackbarProvider
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                Components={{
                    error: WrappingSnackbarContent,
                    success: WrappingSnackbarContent,
                    warning: WrappingSnackbarContent,
                    info: WrappingSnackbarContent,
                }}
                action={(snackbarId) => (
                    <IconButton
                        sx={{
                            'svg path': {
                                fill: `${theme.palette.neutral.black} !important`,
                            },
                        }}
                        onClick={() => closeSnackbar(snackbarId)}
                    >
                        <CloseOutlined />
                    </IconButton>
                )}
            />
        </ThemeProvider>
    );
}
